import { useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { getOrCreatePushSubscription, savePushSubscription } from "@/lib/web-push";

/**
 * Auto-registers the browser for Web Push when the user is authenticated and
 * has previously granted notification permission. Safe to call on every render.
 * Also transparently rotates subscriptions minted under an old VAPID key.
 */
export function useWebPushSubscription() {
  useEffect(() => {
    const subscribe = async () => {
      try {
        if (
          typeof window === "undefined" ||
          !("serviceWorker" in navigator) ||
          !("PushManager" in window) ||
          !("Notification" in window)
        ) {
          return;
        }

        // Skip in iframes (preview environments)
        const isInIframe = (() => {
          try { return window.self !== window.top; } catch { return true; }
        })();
        if (isInIframe) return;

        // Only auto-subscribe if permission is already granted.
        // Manual enable lives in NotificationSettings via usePushNotifications.
        if (Notification.permission !== "granted") return;

        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        const registration = await navigator.serviceWorker.ready;
        const subscription = await getOrCreatePushSubscription(registration);
        await savePushSubscription(user.id, subscription);
      } catch (err) {
        console.error("[WebPush] auto-subscribe failed:", err);
      }
    };

    subscribe();
  }, []);
}
